/**
 * QuestionSetSelect component - Display list of published question sets
 * Requirements: 1.1 - Display question set list on access
 */
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  Chip,
  Stack,
} from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';
import { PublishedQuestionSetSummary } from '../types';

interface QuestionSetSelectProps {
  questionSets: PublishedQuestionSetSummary[];
  isLoading: boolean;
  error: string | null;
  onSelect: (questionSet: PublishedQuestionSetSummary) => void;
}

export const QuestionSetSelect = ({
  questionSets,
  isLoading,
  error,
  onSelect,
}: QuestionSetSelectProps) => {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (questionSets.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <QuizIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography color="text.secondary">
          現在利用可能な質問セットがありません
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 3 }}>
        質問セットを選択
      </Typography>
      <List sx={{ p: 0 }} aria-label="質問セット一覧">
        {questionSets.map((questionSet) => (
          <ListItem key={questionSet.id} sx={{ p: 0, mb: 2 }}>
            <Card
              sx={{
                width: '100%',
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              <CardActionArea
                onClick={() => onSelect(questionSet)}
                aria-label={`${questionSet.title}を選択`}
              >
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                  >
                    <Box>
                      <Typography variant="h6" component="h2">
                        {questionSet.title}
                      </Typography>
                      {questionSet.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {questionSet.description}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={`${questionSet.questionCount}問`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};
